import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Filter, Crown, Check, X, Sparkles, 
  Shield, Clock, Heart, Users, RefreshCw, Lock,
  ChevronRight, Star, Zap, MapPin, Cigarette, Leaf,
  Pill, Calendar, Activity, UserCheck, Home, HandHeart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ELITE_TIERS = ["recovery_elite"];

const AdvancedFiltersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [currentFilters, setCurrentFilters] = useState({
    // Recovery Filters
    min_cleantime_days: null,
    recovery_programs: [],
    recovery_terminology: null,
    relationship_intent: null,
    verified_only: false,
    // New Filters
    max_distance: null,
    spirituality: [],
    activity_level: null,
    has_kids: null,
    wants_kids: null,
    smoking: null,
    cannabis: null,
    mat_friendly: null,
    meeting_frequency: [],
    // Sponsor & Support Filters
    sponsor_status: null,
    is_sponsor: null,
    home_group: null,
    partner_sponsor_importance: null
  });
  const [priorityStatus, setPriorityStatus] = useState(null);
  const [activeSection, setActiveSection] = useState("recovery");

  const isElite = ELITE_TIERS.includes(user?.subscription_tier);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [optionsRes, priorityRes] = await Promise.all([
        api.getFilterOptions(),
        api.getPriorityStatus()
      ]);
      
      setFilterOptions(optionsRes.data.options);
      setPriorityStatus(priorityRes.data);
      
      if (isElite) {
        try {
          const filtersRes = await api.getCurrentFilters();
          setCurrentFilters(prev => ({ ...prev, ...filtersRes.data.filters }));
        } catch (err) {
          // No filters set yet
        }
      }
    } catch (error) {
      console.error("Failed to load filter options:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilters = async () => {
    setSaving(true);
    try {
      await api.updateAdvancedFilters(currentFilters);
      toast.success("Filters saved! Your next matches will reflect these preferences.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save filters");
    } finally {
      setSaving(false);
    }
  };

  const handleClearFilters = async () => {
    setSaving(true);
    try {
      await api.clearAdvancedFilters();
      setCurrentFilters({
        min_cleantime_days: null,
        recovery_programs: [],
        recovery_terminology: null,
        relationship_intent: null,
        verified_only: false,
        max_distance: null,
        spirituality: [],
        activity_level: null,
        has_kids: null,
        wants_kids: null,
        smoking: null,
        cannabis: null,
        mat_friendly: null,
        meeting_frequency: [],
        sponsor_status: null,
        is_sponsor: null,
        home_group: null,
        partner_sponsor_importance: null
      });
      toast.success("All filters cleared!");
    } catch (error) {
      toast.error("Failed to clear filters");
    } finally {
      setSaving(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    const array = currentFilters[field] || [];
    if (array.includes(item)) {
      setCurrentFilters({
        ...currentFilters,
        [field]: array.filter(i => i !== item)
      });
    } else {
      setCurrentFilters({
        ...currentFilters,
        [field]: [...array, item]
      });
    }
  };

  const countActiveFilters = () => {
    let count = 0;
    if (currentFilters.min_cleantime_days) count++;
    if (currentFilters.recovery_programs?.length > 0) count++;
    if (currentFilters.recovery_terminology) count++;
    if (currentFilters.relationship_intent) count++;
    if (currentFilters.verified_only) count++;
    if (currentFilters.max_distance) count++;
    if (currentFilters.spirituality?.length > 0) count++;
    if (currentFilters.activity_level) count++;
    if (currentFilters.has_kids) count++;
    if (currentFilters.wants_kids) count++;
    if (currentFilters.smoking) count++;
    if (currentFilters.cannabis) count++;
    if (currentFilters.mat_friendly) count++;
    if (currentFilters.meeting_frequency?.length > 0) count++;
    if (currentFilters.sponsor_status) count++;
    if (currentFilters.is_sponsor) count++;
    if (currentFilters.home_group) count++;
    if (currentFilters.partner_sponsor_importance) count++;
    return count;
  };

  // Premium gate screen
  if (!isElite) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="filters-locked">
        <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Advanced Filters</h1>
          </div>
        </div>
        
        <div className="p-6 text-center mt-8">
          <div className="w-20 h-20 bg-[#FAB710]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-[#FAB710]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Recovery Elite Feature</h2>
          <p className="text-gray-600 mb-6 max-w-sm mx-auto">
            Unlock 14 advanced filters to find exactly who you're looking for.
          </p>
          
          {/* Priority Status Card */}
          {priorityStatus && (
            <div className="bg-white rounded-2xl p-5 shadow-sm mb-6 text-left max-w-sm mx-auto">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FAB710]" />
                Your Visibility Status
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Priority Boost</span>
                <span className="font-semibold text-gray-800">+{priorityStatus.priority_boost}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-gray-600">Visibility Increase</span>
                <span className="font-semibold text-gray-800">{priorityStatus.visibility_increase}</span>
              </div>
              <p className="text-sm text-gray-500 mt-3">{priorityStatus.description}</p>
            </div>
          )}
          
          <Button
            onClick={() => navigate("/subscription")}
            className="bg-[#FAB710] hover:bg-[#e8a50f] text-white font-semibold px-8"
            data-testid="upgrade-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Recovery Elite
          </Button>
          
          {/* Feature Preview */}
          <div className="mt-8 text-left max-w-sm mx-auto">
            <h3 className="font-semibold text-gray-700 mb-4">Elite Filters Include:</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4 text-[#1C3A7A]" />
                <span>Cleantime</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-[#1C3A7A]" />
                <span>Distance</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4 text-[#1C3A7A]" />
                <span>Recovery programs</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Sparkles className="w-4 h-4 text-[#1C3A7A]" />
                <span>Spirituality</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Heart className="w-4 h-4 text-[#1C3A7A]" />
                <span>Relationship intent</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <UserCheck className="w-4 h-4 text-[#1C3A7A]" />
                <span>Has sponsor</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Home className="w-4 h-4 text-[#1C3A7A]" />
                <span>Home group</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <HandHeart className="w-4 h-4 text-[#1C3A7A]" />
                <span>Sponsors others</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Activity className="w-4 h-4 text-[#1C3A7A]" />
                <span>Activity level</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4 text-[#1C3A7A]" />
                <span>Children</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Cigarette className="w-4 h-4 text-[#1C3A7A]" />
                <span>Smoking</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Leaf className="w-4 h-4 text-[#1C3A7A]" />
                <span>Cannabis</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Pill className="w-4 h-4 text-[#1C3A7A]" />
                <span>MAT-friendly</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4 text-[#1C3A7A]" />
                <span>Meeting frequency</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Shield className="w-4 h-4 text-[#1C3A7A]" />
                <span>Verified only</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FAB710]/30 border-t-[#FAB710] rounded-full animate-spin" />
      </div>
    );
  }

  const sections = [
    { id: "recovery", label: "Recovery", icon: <Heart className="w-4 h-4" /> },
    { id: "support", label: "Support", icon: <UserCheck className="w-4 h-4" /> },
    { id: "location", label: "Location", icon: <MapPin className="w-4 h-4" /> },
    { id: "lifestyle", label: "Lifestyle", icon: <Users className="w-4 h-4" /> },
    { id: "substances", label: "Substances", icon: <Pill className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-28" data-testid="advanced-filters-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Advanced Filters</h1>
          </div>
          <div className="flex items-center gap-2 bg-[#FAB710] px-3 py-1 rounded-full">
            <Crown className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Elite</span>
          </div>
        </div>
        
        {/* Priority Status */}
        {priorityStatus && (
          <div className="bg-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#FAB710]" />
                <span className="text-white font-medium">{priorityStatus.badge}</span>
              </div>
              <span className="text-white/80 text-sm">+{priorityStatus.priority_boost} priority boost</span>
            </div>
            <p className="text-white/70 text-sm mt-1">{priorityStatus.description}</p>
          </div>
        )}

        {/* Active Filters Count */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-white/70 text-sm">{countActiveFilters()} filters active</span>
          <button 
            onClick={handleClearFilters}
            className="text-[#FAB710] text-sm font-medium"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="sticky top-0 bg-white shadow-sm z-10 px-4 py-3">
        <div className="flex gap-2 overflow-x-auto">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeSection === section.id
                  ? "bg-[#1C3A7A] text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {section.icon}
              {section.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* RECOVERY SECTION */}
        {activeSection === "recovery" && (
          <>
            {/* Sobriety Time Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#1C3A7A]" />
                Minimum Sobriety Time
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {filterOptions?.sobriety_ranges?.map((range, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentFilters({
                      ...currentFilters,
                      min_cleantime_days: currentFilters.min_cleantime_days === range.min_days ? null : range.min_days
                    })}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.min_cleantime_days === range.min_days
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recovery Programs Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1C3A7A]" />
                Recovery Programs
              </h3>
              <div className="flex flex-wrap gap-2">
                {filterOptions?.recovery_programs?.map((program, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleArrayItem("recovery_programs", program)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      currentFilters.recovery_programs?.includes(program)
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {program}
                  </button>
                ))}
              </div>
            </div>

            {/* Recovery Terminology */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#1C3A7A]" />
                Recovery Terminology
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "clean", label: "Clean" },
                  { value: "sober", label: "Sober" },
                  { value: "in_recovery", label: "In Recovery" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, recovery_terminology: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.recovery_terminology === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Meeting Frequency */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1C3A7A]" />
                Meeting Frequency
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {filterOptions?.meeting_frequency?.map((freq, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleArrayItem("meeting_frequency", freq.value)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.meeting_frequency?.includes(freq.value)
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {freq.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Relationship Intent */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#1C3A7A]" />
                Looking For
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "friendship", label: "Friendship" },
                  { value: "dating", label: "Dating" },
                  { value: "long_term", label: "Long-term" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, relationship_intent: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.relationship_intent === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* SUPPORT SECTION */}
        {activeSection === "support" && (
          <>
            {/* Has Sponsor Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#1C3A7A]" />
                Sponsor Status
              </h3>
              <p className="text-gray-500 text-sm mb-3">Filter by whether they have a sponsor</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Has sponsor" },
                  { value: "looking", label: "Looking for one" },
                  { value: "no", label: "No sponsor" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, sponsor_status: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.sponsor_status === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`sponsor-status-${option.value || 'any'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Is a Sponsor Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <HandHeart className="w-5 h-5 text-[#1C3A7A]" />
                Sponsors Others
              </h3>
              <p className="text-gray-500 text-sm mb-3">Filter by whether they sponsor others</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Yes, sponsors others" },
                  { value: "open_to_it", label: "Open to sponsoring" },
                  { value: "not_yet", label: "Not yet" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, is_sponsor: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.is_sponsor === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`is-sponsor-${option.value || 'any'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Home Group Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Home className="w-5 h-5 text-[#1C3A7A]" />
                Home Group
              </h3>
              <p className="text-gray-500 text-sm mb-3">Filter by home group status</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Has home group" },
                  { value: "multiple", label: "Multiple groups" },
                  { value: "looking", label: "Looking for one" },
                  { value: "no", label: "No home group" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, home_group: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.home_group === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`home-group-${option.value || 'any'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Partner Sponsor Importance */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#1C3A7A]" />
                Values Sponsor Relationship
              </h3>
              <p className="text-gray-500 text-sm mb-3">How important sponsorship is to them</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "very_important", label: "Very important" },
                  { value: "somewhat_important", label: "Somewhat" },
                  { value: "not_important", label: "Not important" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, partner_sponsor_importance: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.partner_sponsor_importance === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`sponsor-importance-${option.value || 'any'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* LOCATION SECTION */}
        {activeSection === "location" && (
          <>
            {/* Distance Filter */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1C3A7A]" />
                Maximum Distance
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {filterOptions?.distance_options?.map((dist, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentFilters({
                      ...currentFilters,
                      max_distance: currentFilters.max_distance === dist.value ? null : dist.value
                    })}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.max_distance === dist.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {dist.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Activity Level */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#1C3A7A]" />
                Activity Level
              </h3>
              <p className="text-gray-500 text-sm mb-3">Only show users who have been active recently</p>
              <div className="grid grid-cols-2 gap-2">
                {filterOptions?.activity_level?.map((level, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentFilters({
                      ...currentFilters,
                      activity_level: currentFilters.activity_level === level.value ? null : level.value
                    })}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.activity_level === level.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Verified Only */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#1C3A7A]" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Verified Profiles Only</h3>
                    <p className="text-gray-500 text-sm">Only show verified members</p>
                  </div>
                </div>
                <button
                  onClick={() => setCurrentFilters({...currentFilters, verified_only: !currentFilters.verified_only})}
                  className={`w-12 h-7 rounded-full transition-all ${
                    currentFilters.verified_only ? "bg-[#1C3A7A]" : "bg-gray-300"
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    currentFilters.verified_only ? "translate-x-6" : "translate-x-1"
                  }`} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* LIFESTYLE SECTION */}
        {activeSection === "lifestyle" && (
          <>
            {/* Spirituality */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#1C3A7A]" />
                Spirituality
              </h3>
              <p className="text-gray-500 text-sm mb-3">Important for 12-step program compatibility</p>
              <div className="flex flex-wrap gap-2">
                {filterOptions?.spirituality?.map((spirit, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleArrayItem("spirituality", spirit)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      currentFilters.spirituality?.includes(spirit)
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {spirit.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            {/* Children - Has Kids */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1C3A7A]" />
                Has Children
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, has_kids: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.has_kids === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Children - Wants Kids */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#1C3A7A]" />
                Wants Children
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                  { value: "maybe", label: "Maybe" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, wants_kids: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.wants_kids === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* SUBSTANCES SECTION */}
        {activeSection === "substances" && (
          <>
            {/* Smoking */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Cigarette className="w-5 h-5 text-[#1C3A7A]" />
                Smoking
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "never", label: "Never" },
                  { value: "trying_to_quit", label: "Trying to quit" },
                  { value: "socially", label: "Socially" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, smoking: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.smoking === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Cannabis */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Leaf className="w-5 h-5 text-[#1C3A7A]" />
                Cannabis
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "never", label: "Never" },
                  { value: "medical_only", label: "Medical only" },
                  { value: "socially", label: "Socially" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, cannabis: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.cannabis === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* MAT Friendly */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Pill className="w-5 h-5 text-[#1C3A7A]" />
                MAT-Friendly
              </h3>
              <p className="text-gray-500 text-sm mb-3">Medication-Assisted Treatment (Suboxone, Methadone, etc.)</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: null, label: "Any" },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" }
                ].map(option => (
                  <button
                    key={option.value || "any"}
                    onClick={() => setCurrentFilters({...currentFilters, mat_friendly: option.value})}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      currentFilters.mat_friendly === option.value
                        ? "bg-[#1C3A7A] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          disabled={saving}
          className="flex-1"
          data-testid="clear-filters-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Clear All
        </Button>
        <Button
          onClick={handleSaveFilters}
          disabled={saving}
          className="flex-1 bg-[#1C3A7A] hover:bg-[#15305f]"
          data-testid="save-filters-btn"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Filters ({countActiveFilters()})
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdvancedFiltersPage;
